import React, { FC, useRef, useState, useEffect } from 'react';
import { UploadIcon } from 'cokitchen-icons';
import { getFileSize, isFCShopCategory } from 'utils/functions';
import * as XLSX from 'xlsx';
import useNewMealXlsContext from 'contexts/new-meal-xls.context';
import { ENUM_MEAL_CATEGORY_LAYOUT, ENUM_XLS_SHEET_TITLES } from 'utils/enum';

type XlsUploaderProps = {
  title?: string;
  setMeals: (arg: any) => void;
  file: File | null;
  setFile: (item: File | null) => void;
};
const XlsUploader: FC<XlsUploaderProps> = ({
  title = 'Upload Xls File',
  setMeals,
  file,
  setFile
}) => {
  const [percent, setPercent] = useState(0);
  const [timeLeft] = useState('');
  const [uploading, setUploading] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [allowDrop, setAllowDrop] = useState(true);
  const { baseAddonCategories, baseMeals } = useNewMealXlsContext();
  const handleFileDrop = (e: any) => {
    if (allowDrop) {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files) setFile(files[0]);
    }
  };
  const isFcShop = isFCShopCategory();

  const handleDragOver = (e: any) => {
    if (allowDrop) e.preventDefault();
  };
  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const handleFileUpload = (e: any) => {
    setUploading(true);
    setHasUploaded(false);
    const reader = new FileReader();
    reader.readAsBinaryString(e.target.files[0]);
    setFile(e.target.files[0]);
    reader.onload = (e: any) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const menuItemsName = ENUM_XLS_SHEET_TITLES.MENU_ITEMS;
      const addonCategoryMappingName = ENUM_XLS_SHEET_TITLES.ADDON_CATEGORY_MAPPING;
      const addonMappingName = ENUM_XLS_SHEET_TITLES.ADDON_MAPPING;
      const addItemsToShop = ENUM_XLS_SHEET_TITLES.SHOP_ITEMS;
      const menuItems = getCleanRows(XLSX.utils.sheet_to_json(workbook.Sheets[menuItemsName]));
      const shopItems = getCleanRows(XLSX.utils.sheet_to_json(workbook.Sheets[addItemsToShop]));
      const addonCategoryMapping = getCleanRows(
        XLSX.utils.sheet_to_json(workbook.Sheets[addonCategoryMappingName])
      );
      const addonMapping = getCleanRows(
        XLSX.utils.sheet_to_json(workbook.Sheets[addonMappingName])
      );

      if (isFcShop && shopItems.length > 0) {
        setMeals(shopItems.map((raw) => transformRawToItem(raw)));
        setHasUploaded(true);
        setUploading(false);
      }

      matchAddons(addonMapping, addonCategoryMapping, menuItems);
    };
  };

  const transformRawToItem = (raw: any) => {
    return {
      id: Number(raw['No']),
      name: raw['Item Name'],
      description: raw['Item Description'] || '',
      category: raw['Category Name'],
      price: Number(raw['Item Price']),
      weight: raw['Volume/Weight'],
      size: raw['Item Size (S/M/L)'],
      imageUrl: raw['Image/Image Url']
    };
  };

  const getCleanRows = (rows: Array<Record<string, any>>) => {
    return rows.reduce((rows: Array<Record<string, any>>, row: Record<string, any>) => {
      const cleanRow: Record<string, any> = {};
      for (const key in row) {
        if (typeof row[key] === 'string') {
          cleanRow[key] = row[key].trim();

          if (['Item Name', 'Category Name'].includes(key.trim())) {
            cleanRow[key] = cleanRow[key]?.replace(/[^a-zA-Z0-9 ]/g, '');
          }
        } else cleanRow[key] = row[key];
      }
      rows.push(cleanRow);
      return rows;
    }, []);
  };

  const matchAddons = (addons: any, addonCategories: any, menuItems: any) => {
    const list: any = [];
    addonCategories.map((cats: any) => {
      let newCat = cats;
      const addonList = addons.filter((addon: any) => {
        return cats['Category Name'] === addon['Ref']?.replace(/_/g, '');
      });
      newCat.addons = addonList;
      list.push(newCat);
      newCat = [];
    });
    matchMenuItemsWithCategory(list, menuItems);
  };

  const getCategoryOptions = (list: any) => {
    const mySet = new Set();
    for (let i = 0; i < list.length; i++) {
      if (list[i]['Category Name'] !== undefined) mySet.add(list[i]['Category Name']);
    }

    return Array.from(mySet);
  };

  const matchMenuItemsWithCategory = (list: any, menuItems: any) => {
    const newMenuItems = [];

    for (let i = 0; i < menuItems.length; i++) {
      if (menuItems[i]?.['Item Name']) {
        const newMenuItem: any = {
          name: menuItems[i]['Item Name'],
          budget_combo: menuItems[i]['Budget Combo'],
          quantity: menuItems[i]['Quantity'],
          base_meal_id: baseMeals.find((meal) => meal.name === menuItems[i]['Base Meal'])?.id,
          description: menuItems[i]['Item Description'] || '',
          meal_category: {
            name: menuItems[i]['Category Name']
          },
          meal_category_horizontal:
            menuItems[i]['Category Layout'].toLowerCase().trim('') ===
            ENUM_MEAL_CATEGORY_LAYOUT.HORIZONTAL,
          preparation_time: menuItems[i]['Prep Time (mins)']
            ? String(menuItems[i]['Prep Time (mins)'])
            : '',
          image: menuItems[i]['Image Url'] ?? menuItems[i]['Image']
        };
        newMenuItem.meal_categories = getCategoryOptions(menuItems);
        newMenuItem.addons = [];
        const this_data = Object.values(menuItems[i]);
        for (let j = 9; j < this_data.length; j++) {
          const categories = list?.find((item: any) => item['Addon Category'] === this_data[j]);
          const defaultAddons =
            categories &&
            Object.entries(categories)
              .map((entry) => {
                if (entry[0].includes('Default')) {
                  return entry[1];
                } else {
                  return null;
                }
              })
              .filter((item) => item !== null);

          const activeBaseAddonCat = baseAddonCategories?.find(
            (item) =>
              item.slug?.replace(/_/g, '') === categories?.['Category Name']?.replace(/_/g, '')
          );

          const addon_options = categories?.addons?.map((addon: any) => addon?.['Addon Name']);
          if (categories && addon_options) {
            newMenuItem.addons.push({
              base_addon_category_id: activeBaseAddonCat?.id,
              meal_category_selection_type: {
                name: activeBaseAddonCat?.meal_category_selection_type
              },

              min_selection_no: activeBaseAddonCat?.min_selection_no,
              max_selection_no: activeBaseAddonCat?.max_selection_no,
              name: activeBaseAddonCat?.name,
              summary: activeBaseAddonCat?.summary,
              id: activeBaseAddonCat?.id,
              addons: categories?.addons?.map((addon: any) => {
                return {
                  default: defaultAddons.includes(addon?.['Addon number']),
                  addon_meal_id: baseMeals.find((meal) => meal.name === addon?.['Base Meal'])?.id,
                  quantity: addon?.['Quantity'],
                  name: addon?.['Addon Name'],
                  ref: addon?.['Ref']
                };
              })
            });
          }
        }

        newMenuItems.push(newMenuItem);
        setMeals(newMenuItems);
        setHasUploaded(true);
        setUploading(false);
      }
    }
  };

  useEffect(() => {
    if (uploading) {
      setAllowDrop(false);
    } else {
      setPercent(0);
      setAllowDrop(true);
    }
  }, [uploading]);

  return (
    <>
      <div className='max-h-[330px] overflow-auto pt-4 pb-2 px-6 rounded-[6px] border border-[#EDEDEE]  space-y-5'>
        <h4 className='font-medium'>{title}</h4>
        <div
          role='button'
          className={`border border-dashed border-[#D9D9D9] h-[100px] flex items-center justify-center gap-1 w-full rounded-md text-xs ${
            uploading && 'cursor-not-allowed '
          }`}
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          onClick={() => {
            if (!uploading) fileUploadRef?.current?.click();
          }}>
          <UploadIcon className='mr-3' />
          <p className='text-gray-4'> Drop {!file ? 'your' : 'another'} file here or </p>
          <span className={`${uploading ? 'text-gray-4 opacity-40' : 'font-medium text-red-5'}`}>
            browse
          </span>
        </div>
        {file ? (
          <div className='bg-[#F5F4F4] rounded px-8 py-3 space-y-5'>
            <div className='flex items-center gap-6 flex-wrap'>
              <div className='w-[50px] h-[50px] rounded-full bg-white p-2'>
                <div
                  className='h-[100%] w-[100%] rounded-[100%]'
                  style={{
                    //backgroundImage: `url(${getImgSrc})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
              </div>

              <div className='grow space-y-2'>
                <label className='text-sm gap-2 font-medium text-[#4B4D4E]'>{file.name}</label>
                <div className='text-xs flex text-gray-5 items-center gap-[7px]'>
                  <p>{getFileSize(file.size)}</p>
                  <div className='w-1 h-1 bg-gray-5 rounded-full' />
                  {uploading && <p>{timeLeft} left</p>}
                </div>
              </div>
              {hasUploaded && (
                <span className='self-end text-xs text-gray-5'>Upload Complete!</span>
              )}
              {uploading && <span className='self-end text-xs text-gray-5'>{percent}%</span>}
            </div>
            {uploading && (
              <div className='relative overflow-hidden h-[6px] rounded-md w-full bg-white'>
                <div
                  style={{
                    height: '100%',
                    width: `${percent}%`,
                    transition: 'width .8s'
                  }}
                  className={`bg-red-5 rounded-full`}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>

      <input
        ref={fileUploadRef}
        type='file'
        accept='.xlsx, .xls'
        hidden
        onChange={handleFileUpload}
      />
    </>
  );
};

export default XlsUploader;
